import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Search, 
  Filter, 
  MapPin, 
  Users, 
  Star, 
  Bookmark, 
  BookmarkCheck,
  MessageSquare,
  ChevronDown,
  ArrowLeft,
  Trophy,
  GraduationCap,
  Loader2
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { locationService } from '@/services/venueClaimService';
import { getPositionsForSport, SPORT_POSITIONS } from '@/data/sportsPositions';
import { 
  checkAgentCanContactAthlete, 
  recordAgentInterestInMinor 
} from '@/services/minorContactProtectionService';

interface Athlete {
  id: string;
  name: string;
  username: string;
  photoUrl: string | null;
  sport: string | null;
  position: string | null;
  ageGroup: string | null;
  location: string | null;
  country: string | null;
  stateProvince: string | null;
  city: string | null;
  userType: 'internal' | 'supabase';
  skillLevel: string | null;
  jerseyNumber: number | null;
  role: string | null;
}

const AgentAthleteDiscovery = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [filters, setFilters] = useState({
    sport: '',
    position: '',
    ageGroup: '',
    country: '',
    stateProvince: '',
    city: '',
    role: '',
  });
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [savedAthletes, setSavedAthletes] = useState<Set<string>>(new Set());
  const [savingAthlete, setSavingAthlete] = useState<string | null>(null);
  const [availableStates, setAvailableStates] = useState<string[]>([]);

  // Update available states when country changes
  useEffect(() => {
    if (filters.country && filters.country !== 'all') {
      setAvailableStates(locationService.getStatesProvinces(filters.country));
    } else {
      setAvailableStates([]);
    }
  }, [filters.country]);

  // Handle country change - reset state and city
  const handleCountryChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      country: value,
      stateProvince: '',
      city: ''
    }));
  };

  // Get agent session
  const getAgentSession = () => {
    const sessionData = localStorage.getItem('foundingAgentSession') || sessionStorage.getItem('foundingAgentSession');
    if (sessionData) {
      try {
        return JSON.parse(sessionData);
      } catch {
        return null;
      }
    }
    return null;
  };

  // Fetch athletes from both internal_users and profiles
  useEffect(() => {
    const fetchAthletes = async () => {
      setLoading(true);
      try {
        // Fetch internal users with role = 'player'
        const { data: internalUsers, error: internalError } = await supabase
          .from('internal_users')
          .select('id, username, role, is_active')
          .eq('role', 'player')
          .eq('is_active', true);

        if (internalError) {
          console.error('Error fetching internal users:', internalError);
        }

        // Fetch internal user profiles
        const internalUserIds = internalUsers?.map(u => u.id) || [];
        let internalProfiles: any[] = [];
        if (internalUserIds.length > 0) {
          const { data: profiles } = await supabase
            .from('internal_user_profiles')
            .select('*')
            .in('user_id', internalUserIds);
          internalProfiles = profiles || [];
        }

        // Fetch Supabase profiles with role = 'player'
        const { data: supabaseProfiles, error: supabaseError } = await supabase
          .from('profiles')
          .select('*')
          .eq('role', 'player');

        if (supabaseError) {
          console.error('Error fetching Supabase profiles:', supabaseError);
        }

        // Normalize and merge both data sources
        const normalizedAthletes: Athlete[] = [];

        // Process internal users
        if (internalUsers) {
          for (const user of internalUsers) {
            const profile = internalProfiles.find(p => p.user_id === user.id);
            normalizedAthletes.push({
              id: user.id,
              name: profile?.first_name && profile?.last_name 
                ? `${profile.first_name} ${profile.last_name}` 
                : user.username,
              username: user.username,
              photoUrl: profile?.profile_photo_url || null,
              sport: profile?.primary_sport || null,
              position: profile?.position || null,
              ageGroup: profile?.age_group || null,
              location: profile?.location || null,
              country: profile?.country || null,
              stateProvince: profile?.state_province || profile?.state || null,
              city: profile?.city || null,
              userType: 'internal',
              skillLevel: profile?.skill_level || null,
              jerseyNumber: profile?.jersey_number || null,
              role: user.role || 'player',
            });
          }
        }

        // Process Supabase profiles
        if (supabaseProfiles) {
          for (const profile of supabaseProfiles) {
            normalizedAthletes.push({
              id: profile.id,
              name: profile.first_name && profile.last_name 
                ? `${profile.first_name} ${profile.last_name}` 
                : profile.username || 'Unknown',
              username: profile.username || '',
              photoUrl: profile.profile_photo_url || null,
              sport: profile.primary_sport || null,
              position: profile.position || null,
              ageGroup: profile.age_group || null,
              location: profile.city || profile.state || null,
              country: (profile as any).country || null,
              stateProvince: profile.state || null,
              city: profile.city || null,
              userType: 'supabase',
              skillLevel: profile.skill_level || null,
              jerseyNumber: profile.jersey_number || null,
              role: profile.role || 'player',
            });
          }
        }

        setAthletes(normalizedAthletes);

        // Fetch saved athletes for this agent
        const agentSession = getAgentSession();
        if (agentSession?.userId) {
          const { data: saved } = await supabase
            .from('agent_saved_athletes')
            .select('athlete_user_id')
            .eq('agent_user_id', agentSession.userId);

          if (saved) {
            setSavedAthletes(new Set(saved.map(s => s.athlete_user_id)));
          }
        }
      } catch (error) {
        console.error('Error fetching athletes:', error);
        toast.error('Failed to load athletes');
      } finally {
        setLoading(false);
      }
    };

    fetchAthletes();
  }, []);

  // Filter athletes based on search and filters
  const filteredAthletes = useMemo(() => {
    return athletes.filter(athlete => {
      // Search query filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          athlete.name.toLowerCase().includes(query) ||
          athlete.username.toLowerCase().includes(query) ||
          (athlete.sport?.toLowerCase().includes(query) ?? false) ||
          (athlete.location?.toLowerCase().includes(query) ?? false) ||
          (athlete.position?.toLowerCase().includes(query) ?? false);
        if (!matchesSearch) return false;
      }

      // Sport filter
      if (filters.sport && filters.sport !== 'all') {
        if (!athlete.sport?.toLowerCase().includes(filters.sport.toLowerCase())) {
          return false;
        }
      }

      // Age group filter
      if (filters.ageGroup && filters.ageGroup !== 'all') {
        if (!athlete.ageGroup?.toLowerCase().includes(filters.ageGroup.toLowerCase())) {
          return false;
        }
      }

      // Country filter
      if (filters.country && filters.country !== 'all') {
        if (!athlete.country?.toLowerCase().includes(filters.country.toLowerCase()) &&
            !athlete.location?.toLowerCase().includes(filters.country.toLowerCase())) {
          return false;
        }
      }

      // State/Province filter
      if (filters.stateProvince && filters.stateProvince !== 'all') {
        if (!athlete.stateProvince?.toLowerCase().includes(filters.stateProvince.toLowerCase()) &&
            !athlete.location?.toLowerCase().includes(filters.stateProvince.toLowerCase())) {
          return false;
        }
      }

      // City filter (text search)
      if (filters.city) {
        if (!athlete.city?.toLowerCase().includes(filters.city.toLowerCase()) &&
            !athlete.location?.toLowerCase().includes(filters.city.toLowerCase())) {
          return false;
        }
      }

      // Position filter
      if (filters.position) {
        if (!athlete.position?.toLowerCase().includes(filters.position.toLowerCase())) {
          return false;
        }
      }

      // Role filter
      if (filters.role && filters.role !== 'all') {
        if (!athlete.role?.toLowerCase().includes(filters.role.toLowerCase())) {
          return false;
        }
      }

      return true;
    });
  }, [athletes, searchQuery, filters]);

  // Handle bookmark/save athlete
  const handleSaveAthlete = async (athlete: Athlete) => {
    const agentSession = getAgentSession();
    if (!agentSession?.userId) {
      toast.error('Please log in to save athletes');
      return;
    }

    setSavingAthlete(athlete.id);
    try {
      const isSaved = savedAthletes.has(athlete.id);

      if (isSaved) {
        // Remove from saved
        const { error } = await supabase
          .from('agent_saved_athletes')
          .delete()
          .eq('agent_user_id', agentSession.userId)
          .eq('athlete_user_id', athlete.id);

        if (error) throw error;

        setSavedAthletes(prev => {
          const next = new Set(prev);
          next.delete(athlete.id);
          return next;
        });
        toast.success('Athlete removed from saved');
      } else {
        // Add to saved
        const { error } = await supabase
          .from('agent_saved_athletes')
          .insert({
            agent_user_id: agentSession.userId,
            agent_user_type: 'internal',
            athlete_user_id: athlete.id,
            athlete_user_type: athlete.userType,
          });

        if (error) throw error;

        setSavedAthletes(prev => new Set(prev).add(athlete.id));
        toast.success('Athlete saved to your list');
      }
    } catch (error) {
      console.error('Error saving athlete:', error);
      toast.error('Failed to save athlete');
    } finally {
      setSavingAthlete(null);
    }
  };

  // Handle contact athlete
  const handleContactAthlete = async (athlete: Athlete) => {
    const agentSession = getAgentSession();
    if (!agentSession?.userId) {
      toast.error('Please log in to contact athletes');
      return;
    }

    try {
      // Check if agent can contact this athlete (minor protection)
      const contactCheck = await checkAgentCanContactAthlete(
        athlete.id, 
        athlete.userType
      );

      if (!contactCheck.canContact) {
        // Show the minor protection message
        toast.error(contactCheck.message, {
          duration: 6000,
          description: 'Contact the parent instead to discuss NIL opportunities.'
        });

        // Record the blocked attempt
        await recordAgentInterestInMinor(
          agentSession.userId,
          'internal',
          athlete.id,
          athlete.userType
        );
        return;
      }

      // Record the contact in agent_athlete_contacts
      const monthYear = new Date().toISOString().slice(0, 7); // YYYY-MM format
      const { error } = await supabase
        .from('agent_athlete_contacts')
        .insert({
          agent_user_id: agentSession.userId,
          agent_user_type: 'internal',
          athlete_user_id: athlete.id,
          athlete_user_type: athlete.userType,
          contact_type: 'initial_outreach',
          month_year: monthYear,
        });

      if (error) {
        // If duplicate, just show the contact modal anyway
        if (!error.message.includes('duplicate')) {
          console.error('Error recording contact:', error);
        }
      }

      // Navigate to messaging with this athlete
      toast.success(`Initiating contact with ${athlete.name}`);
      // TODO: Open messaging modal or navigate to messaging page
    } catch (error) {
      console.error('Error contacting athlete:', error);
      toast.error('Unable to contact athlete. Please try again.');
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/agent/dashboard')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold">Find Athletes</h1>
            <p className="text-muted-foreground">
              Discover and connect with {athletes.length} athletes on the platform
            </p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, sport, team, location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Button 
            variant="outline" 
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            <Filter className="mr-2 h-4 w-4" />
            Filters
            <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${filtersOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Filters */}
        <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
          <CollapsibleContent>
            <Card className="mb-6">
              <CardContent className="pt-6">
                {/* Row 1: Sport, Position, Age Group, Role */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {/* Sport Filter */}
                  <div className="space-y-2">
                    <Label>Sport</Label>
                    <Select 
                      value={filters.sport}
                      onValueChange={(value) => setFilters({...filters, sport: value, position: ''})}
                    >
                      <SelectTrigger className="border-primary/50">
                        <SelectValue placeholder="All Sports" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover z-50">
                        <SelectItem value="all">All Sports</SelectItem>
                        {Object.keys(SPORT_POSITIONS).map((sport) => (
                          <SelectItem key={sport} value={sport.toLowerCase()}>
                            {sport}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Position Filter - Dynamic based on sport */}
                  <div className="space-y-2">
                    <Label>Position</Label>
                    <Select 
                      value={filters.position}
                      onValueChange={(value) => setFilters({...filters, position: value})}
                      disabled={!filters.sport || filters.sport === 'all'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={filters.sport && filters.sport !== 'all' ? "Select Position" : "Select sport first"} />
                      </SelectTrigger>
                      <SelectContent className="bg-popover z-50 max-h-[300px]">
                        <SelectItem value="all">All Positions</SelectItem>
                        {filters.sport && filters.sport !== 'all' && 
                          getPositionsForSport(filters.sport.charAt(0).toUpperCase() + filters.sport.slice(1)).map((position) => (
                            <SelectItem key={position} value={position.toLowerCase()}>
                              {position}
                            </SelectItem>
                          ))
                        }
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Age Group Filter */}
                  <div className="space-y-2">
                    <Label>Age Group</Label>
                    <Select 
                      value={filters.ageGroup}
                      onValueChange={(value) => setFilters({...filters, ageGroup: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All Ages" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover z-50">
                        <SelectItem value="all">All Ages</SelectItem>
                        <SelectItem value="youth">Youth (Under 18)</SelectItem>
                        <SelectItem value="high-school">High School</SelectItem>
                        <SelectItem value="college">College</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="adult">Adult</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Role Filter */}
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select 
                      value={filters.role}
                      onValueChange={(value) => setFilters({...filters, role: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All Roles" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover z-50">
                        <SelectItem value="all">All Roles</SelectItem>
                        <SelectItem value="player">Player</SelectItem>
                        <SelectItem value="coach">Coach</SelectItem>
                        <SelectItem value="parent">Parent/Guardian</SelectItem>
                        <SelectItem value="fan">Fan</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Row 2: Country, State/Province, City */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {/* Country Filter */}
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select 
                      value={filters.country}
                      onValueChange={handleCountryChange}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="All Countries" />
                      </SelectTrigger>
                      <SelectContent className="bg-popover z-50">
                        <SelectItem value="all">All Countries</SelectItem>
                        {locationService.getCountries().map((country) => (
                          <SelectItem key={country} value={country}>
                            {country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* State/Province Filter */}
                  <div className="space-y-2">
                    <Label>State/Province</Label>
                    <Select 
                      value={filters.stateProvince}
                      onValueChange={(value) => setFilters({...filters, stateProvince: value})}
                      disabled={!filters.country || filters.country === 'all'}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={filters.country && filters.country !== 'all' ? "Select State" : "Select country first"} />
                      </SelectTrigger>
                      <SelectContent className="bg-popover z-50 max-h-[300px]">
                        <SelectItem value="all">All States</SelectItem>
                        {availableStates.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* City Filter (Text Input) */}
                  <div className="space-y-2">
                    <Label>City</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Enter city..."
                        value={filters.city}
                        onChange={(e) => setFilters({...filters, city: e.target.value})}
                        className="pl-10"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setFilters({ 
                      sport: '', 
                      position: '', 
                      ageGroup: '', 
                      country: '', 
                      stateProvince: '', 
                      city: '', 
                      role: '' 
                    })}
                  >
                    Clear Filters
                  </Button>
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <span className="ml-2 text-muted-foreground">Loading athletes...</span>
          </div>
        ) : filteredAthletes.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-xl font-semibold mb-2">No Athletes Found</h3>
              <p className="text-muted-foreground mb-4">
                {athletes.length > 0 
                  ? 'Try adjusting your search criteria or clearing filters'
                  : 'Athletes will appear here as they join the platform and set their profiles to discoverable.'
                }
              </p>
              {filters.sport || filters.ageGroup || filters.country || filters.stateProvince || filters.city || filters.position || filters.role ? (
                <Button 
                  variant="outline"
                  onClick={() => setFilters({ 
                    sport: '', 
                    position: '', 
                    ageGroup: '', 
                    country: '', 
                    stateProvince: '', 
                    city: '', 
                    role: '' 
                  })}
                >
                  Clear All Filters
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              Showing {filteredAthletes.length} of {athletes.length} athletes
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredAthletes.map((athlete) => (
                <Card key={`${athlete.userType}-${athlete.id}`} className="overflow-hidden hover:shadow-xl transition-all duration-300 border-zinc-700/50 bg-zinc-900 hover:border-primary/40 rounded-xl">
                  {/* Photo Area - extends behind player info */}
                  <div className="relative">
                    {/* Photo Container */}
                    <div className="h-56 bg-zinc-800 flex items-center justify-center overflow-hidden">
                      {athlete.photoUrl ? (
                        <img 
                          src={athlete.photoUrl} 
                          alt={athlete.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <>
                          {/* Diagonal Lines Pattern */}
                          <svg className="absolute inset-0 w-full h-56" preserveAspectRatio="none">
                            <line x1="0" y1="0" x2="100%" y2="224" stroke="#3f3f46" strokeWidth="1" />
                            <line x1="100%" y1="0" x2="0" y2="224" stroke="#3f3f46" strokeWidth="1" />
                          </svg>
                          <span className="text-zinc-500 text-sm font-medium tracking-[0.3em] uppercase relative z-10">Photo</span>
                        </>
                      )}
                      
                      {/* Bookmark Button */}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute top-3 right-3 h-9 w-9 bg-zinc-800/80 hover:bg-zinc-700 border border-zinc-600 rounded-md z-20"
                        onClick={() => handleSaveAthlete(athlete)}
                        disabled={savingAthlete === athlete.id}
                      >
                        {savingAthlete === athlete.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                        ) : savedAthletes.has(athlete.id) ? (
                          <BookmarkCheck className="h-4 w-4 text-primary" />
                        ) : (
                          <Bookmark className="h-4 w-4 text-zinc-400" />
                        )}
                      </Button>
                    </div>

                    {/* Player Info Panel - overlays bottom of photo */}
                    <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/2 px-3 z-10">
                      <div className="bg-zinc-800/95 backdrop-blur-sm rounded-lg p-3 border border-zinc-700/50 flex gap-3 items-center">
                        {/* Jersey Number Box */}
                        <div className="flex-shrink-0 w-14 h-14 border-2 border-primary rounded-lg flex items-center justify-center bg-primary/15">
                          <span className="text-2xl font-bold text-primary">
                            {athlete.jerseyNumber ?? '--'}
                          </span>
                        </div>
                        
                        {/* Name & Sport Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-white text-base truncate leading-tight">
                            {athlete.name || 'Player Name'}
                          </h3>
                          <p className="text-xs text-zinc-400 truncate">
                            @{athlete.username || 'username'}
                          </p>
                          <p className="text-xs text-zinc-300 uppercase tracking-wide font-medium mt-1">
                            {athlete.sport || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content below photo - with spacing for overlay */}
                  <CardContent className="pt-12 pb-0 px-4 space-y-3 bg-zinc-900">
                    {/* Badges Row */}
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs gap-1.5 border-zinc-600 bg-zinc-800 text-zinc-300 px-2.5 py-1 rounded-md">
                        <span className="text-zinc-500 font-bold">||</span>
                        {athlete.role || 'Role'}
                      </Badge>
                      <Badge variant="outline" className="text-xs gap-1.5 border-zinc-600 bg-zinc-800 text-zinc-300 px-2.5 py-1 rounded-md">
                        <span>😊</span>
                        {athlete.ageGroup || '18-plus'}
                      </Badge>
                      <Badge variant="outline" className="text-xs gap-1.5 border-zinc-600 bg-zinc-800 text-zinc-300 px-2.5 py-1 rounded-md">
                        <Star className="h-3 w-3 text-zinc-400" />
                        {athlete.skillLevel || 'advanced'}
                      </Badge>
                    </div>

                    {/* 10px spacer above location */}
                    <div className="h-[10px]"></div>

                    {/* Location Bar */}
                    <div className="bg-primary text-zinc-900 py-2 text-center text-sm font-bold uppercase tracking-wide -mx-4 flex items-center justify-center">
                      <span>{athlete.country || 'N/A'}</span>
                      <span className="mx-3 text-zinc-700/70">|</span>
                      <span>{athlete.stateProvince || 'N/A'}</span>
                      <span className="mx-3 text-zinc-700/70">|</span>
                      <span>{athlete.city || 'N/A'}</span>
                    </div>

                    {/* 10px spacer below location */}
                    <div className="h-[10px]"></div>

                    {/* Contact Button */}
                    <Button 
                      variant="outline" 
                      className="w-full border-2 border-primary text-primary hover:bg-primary hover:text-zinc-900 gap-2 font-semibold h-11 rounded-lg transition-all duration-200"
                      onClick={() => handleContactAthlete(athlete)}
                    >
                      <MessageSquare className="h-4 w-4" />
                      Contact
                    </Button>

                    {/* 15px spacer below contact button */}
                    <div className="h-[15px]"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default AgentAthleteDiscovery;
